import { useState } from "react"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faCircleInfo, faUser, faUserTie } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { TranslationKey } from "../../../../../../localization/translations"
import { getOrderPerson } from "../../../../../../../shared/utils/getOrderPerson"
import { MeetingServiceOrder } from "../../meetingServiceTypes"

import { ProductionOrderUsersDialog } from "./ProductionOrderUsersDialog"

const useStyles = makeStyles()((theme) => ({
    metaGroup: {
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "1.5rem",
    },
    metaItem: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
    },
    metaIconWrap: {
        display: "inline-flex",
    },
    metaIcon: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[400],
    },
    metaValue: {
        color: theme.palette.tailwind.slate[800],
        whiteSpace: "nowrap",
    },
    infoButton: {
        padding: 0,
    },
    infoIcon: {
        fontSize: ".75rem",
        color: theme.palette.tailwind.slate[400],
        cursor: "pointer",
        "&:hover": {
            color: theme.palette.tailwind.slate[600],
        },
    },
}))

type MetaItemProps = Readonly<{
    labelId: TranslationKey
    value: string
    icon: IconDefinition
    onInfoClick?: () => void
}>

function MetaItem({ labelId, value, icon, onInfoClick }: MetaItemProps) {
    const { classes } = useStyles()

    return (
        <Box className={classes.metaItem}>
            <LocalizedStrict id={labelId} attrs={{ title: true }}>
                <Tooltip arrow title="">
                    <Box component="span" className={classes.metaIconWrap}>
                        <FontAwesomeIcon icon={icon} className={classes.metaIcon} />
                    </Box>
                </Tooltip>
            </LocalizedStrict>
            <Typography variant="body1" component="span" className={classes.metaValue}>
                {value}
            </Typography>
            {onInfoClick && (
                <IconButton className={classes.infoButton} onClick={onInfoClick}>
                    <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                </IconButton>
            )}
        </Box>
    )
}

type Props = Readonly<{
    order: MeetingServiceOrder
    showSittings?: boolean
}>

export function ProductionOrderCardMetaRow({ order, showSittings = true }: Props) {
    const { classes } = useStyles()
    const [isUsersDialogOpen, setIsUsersDialogOpen] = useState(false)

    const { person: meetingHost } = getOrderPerson(order.organizers)
    const { deliveryDetails } = order

    return (
        <Box className={classes.metaGroup}>
            <Typography variant="body1" component="span" className={classes.metaValue}>
                #{order.id}
            </Typography>
            {showSittings && !!deliveryDetails.sittings && (
                <MetaItem
                    icon={faUser}
                    labelId="meeting-service-production-view-sittings-label"
                    value={String(deliveryDetails.sittings)}
                />
            )}
            {meetingHost?.name && (
                <MetaItem
                    icon={faUserTie}
                    labelId="meeting-service-production-view-host-label"
                    value={meetingHost.name}
                    onInfoClick={() => setIsUsersDialogOpen(true)}
                />
            )}
            <ProductionOrderUsersDialog
                open={isUsersDialogOpen}
                onClose={() => setIsUsersDialogOpen(false)}
                organizers={order.organizers}
            />
        </Box>
    )
}
